import "../styles/settingsDialog.css";
import { FiSun, FiMoon, FiMonitor } from "react-icons/fi";
import { HiOutlineX } from "react-icons/hi";
import { useTheme } from "@/components/theme/useTheme";
import type { Theme } from "@/components/theme/theme-context";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { useEffect, useCallback } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { usePrefersReducedMotion } from "@/hooks/usePrefersReducedMotion";
import { useScrollLock } from "@/hooks/useScrollLock";
import { ACCENT_OPTIONS, selectedAccentId, setAccent, useAccentOption } from "@/components/theme/accent";

type SettingsDialogProps = {
    showMenu: boolean,
    setShowMenu: (value: (((prevState: boolean) => boolean) | boolean)) => void,
}

const themeOptions: { mode: Theme; icon: React.ReactNode }[] = [
    { mode: 'light', icon: <FiSun size={16} /> },
    { mode: 'dark', icon: <FiMoon size={16} /> },
    { mode: 'system', icon: <FiMonitor size={16} /> },
];

const localeOptions = [
    { code: 'en', label: 'EN' },
    { code: 'zh-TW', label: '繁中' },
    { code: 'zh-CN', label: '简中' },
];

export const SettingsDialog = ({ showMenu, setShowMenu }: SettingsDialogProps) => {
    const { t, i18n } = useTranslation();
    const { theme, setTheme, actualTheme } = useTheme();
    const accent = useAccentOption();
    const selectedAccent = selectedAccentId(accent, actualTheme);
    const navigate = useNavigate();
    const { locale } = useParams<{ locale: string }>();
    const prefersReducedMotion = usePrefersReducedMotion();

    const closeMenu = useCallback(() => setShowMenu(false), [setShowMenu]);

    const switchLanguage = (newLocale: string) => {
        i18n.changeLanguage(newLocale);
        const currentPath = window.location.pathname;
        const newPath = currentPath.replace(`/${locale}`, `/${newLocale}`);
        navigate(newPath);
    };

    // Close on Escape key
    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Escape' && showMenu) closeMenu();
        };
        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, [showMenu, closeMenu]);

    // Refcounted lock, shared with the other dialogs so overlapping locks cannot clobber each other.
    useScrollLock(showMenu);

    return (
        <AnimatePresence>
            {showMenu && (
                <>
                    <motion.div
                        className="panel-backdrop settings-panel-backdrop"
                        onClick={closeMenu}
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.25, ease: "easeOut" }}
                    />
                    <motion.div
                        className="panel settings-panel"
                        onClick={(e) => e.stopPropagation()}
                        role="dialog"
                        aria-modal="true"
                        aria-label={t("toggleMenu.settings", "Settings")}
                        initial={prefersReducedMotion ? { opacity: 0 } : { x: "100%" }}
                        animate={prefersReducedMotion ? { opacity: 1 } : { x: 0 }}
                        exit={prefersReducedMotion ? { opacity: 0 } : { x: "100%" }}
                        transition={{ duration: 0.5, ease: [0.76, 0, 0.24, 1] }}
                    >
                        <div className="panel-header">
                            <h2 className="panel-title">{t("toggleMenu.settings", "Settings")}</h2>
                            <button
                                className="panel-close"
                                onClick={closeMenu}
                                aria-label="Close"
                            >
                                <HiOutlineX aria-hidden="true" />
                            </button>
                        </div>

                        <div className="panel-body">
                            {/* Theme */}
                            <div className="panel-block">
                                <p className="panel-label">{t("toggleMenu.theme", "Theme")}</p>
                                <div className="settings-segment">
                                    {themeOptions.map(({ mode, icon }) => (
                                        <button
                                            key={mode}
                                            className={`settings-segment-btn ${theme === mode ? 'is-active' : ''}`}
                                            onClick={() => setTheme(mode)}
                                            aria-label={t(`themeSwitcher.${mode}`)}
                                            aria-pressed={theme === mode}
                                            title={t(`themeSwitcher.${mode}`)}
                                        >
                                            {icon}
                                        </button>
                                    ))}
                                </div>
                            </div>

                            {/* Accent colour */}
                            <div className="panel-block">
                                <p className="panel-label">{t("toggleMenu.accent")}</p>
                                <div className="settings-swatches">
                                    {ACCENT_OPTIONS.map((option) => (
                                        <button
                                            key={option.id}
                                            className={`settings-swatch ${selectedAccent === option.id ? 'is-active' : ''}`}
                                            style={{ background: option[actualTheme] }}
                                            onClick={() => setAccent(option.id)}
                                            aria-label={t(`accentColors.${option.id}`)}
                                            aria-pressed={selectedAccent === option.id}
                                            title={t(`accentColors.${option.id}`)}
                                        />
                                    ))}
                                </div>
                                <p className="settings-swatch-name">{t(`accentColors.${selectedAccent}`)}</p>
                            </div>

                            {/* Language */}
                            <div className="panel-block">
                                <p className="panel-label">{t("common.languageSwitch", "Language")}</p>
                                <div className="settings-segment">
                                    {localeOptions.map(({ code, label }) => (
                                        <button
                                            key={code}
                                            className={`settings-segment-btn settings-segment-btn--text ${i18n.language === code ? 'is-active' : ''}`}
                                            onClick={() => switchLanguage(code)}
                                            aria-pressed={i18n.language === code}
                                        >
                                            {label}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </motion.div>
                </>
            )}
        </AnimatePresence>
    );
}
